import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonBase, IconButton, Stack, Tooltip } from '@mui/material';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { ApiConsentState, type ApiConsentStateItem } from '@agunity/api-v4';

import AgLoading from '../../../stories/AgLoading';
import AgTypography from '../../../stories/Styles/AgTypography';
import Content from '../../../components/ConsentProvider/Content';
import AgSimpleTable from '../../../stories/Components/AgSimpleTable';
import Confirmation from '../../../components/ConsentProvider/Confirmation';
import { AgColor } from '../../../stories/Styles/AgColor';

import { formatDate } from '../../../utils';
import { useModal } from '../../../redux/hooks/modal';
import { selectLocale } from '../../../redux/slices/locales';
import { FALL_BACK_LOCAL } from '../../../components/Language';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getConsentActiveList, patchConsentItemState } from '../../../redux/slices/global';
import * as S from './styled';

const ProfileConsents = () => {
  const messages = useIntl().formatMessage;
  const { openModal, closeModal } = useModal();

  const dispatch = useAppDispatch();
  const defaultLocale = useAppSelector(selectLocale);

  const [consents, setConsents] = useState<ApiConsentStateItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const locale = useMemo(() => defaultLocale ?? FALL_BACK_LOCAL, [defaultLocale]);

  const getConsents = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(getConsentActiveList(locale, true));
      const data = response?.data || [];
      setConsents(data);
    } catch (error) {
      console.error('Failed to get consents: ', error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, locale]);

  const updateConsentState = useCallback(async (consentId: number, state: ApiConsentState) => {
    try {
      await dispatch(patchConsentItemState(consentId, state));
      await getConsents();
    } catch (error) {
      console.error(error);
    } finally {
      closeModal();
    }
  }, [closeModal, dispatch, getConsents]);

  const handleConfirm = useCallback((state: ApiConsentState, consent: ApiConsentStateItem) => {
    openModal({
      autoClose: false,
      title: messages({ id: 'CONSENT_TITLE' }),
      body: <Confirmation />,
      actions: [{
        label: messages({ id: 'CONSENT_ACTIONS_CONTINUE' }),
        onClick: async () => await updateConsentState(consent.id!, state)
      }]
    });
  }, [messages, openModal, updateConsentState]);

  const handleViewConsent = useCallback((consent: ApiConsentStateItem, isCloseOnly?: boolean) => {
    openModal({
      autoClose: false,
      title: consent.name,
      maxHeight: 'calc(100vh - 100px)',
      body: <Content consent={consent} handleConfirm={(state) => handleConfirm(state, consent)} isCloseOnly={isCloseOnly} />
    });
  }, [handleConfirm, openModal]);

  const tableConsents = useMemo(() => consents.map((item) => {
    const isAgreed = item?.givenConsent === ApiConsentState.Agreed;
    const isRejected = item?.givenConsent === ApiConsentState.Rejected;
    const isMandatoryDisabled = item?.isMandatory && isAgreed;
    return {
      name: item?.name!,
      value: (
        <ButtonBase sx={{ p: 1, borderRadius: 1 }} onClick={() => handleViewConsent(item, true)}>
          <AgTypography contentStyle='AP.Paragraph.Small.Link' style={{ fontSize: 10 }}>
            {messages({ id: 'CONSENT_VIEW' })}
          </AgTypography>
        </ButtonBase>
      ),
      thirdValue: (
        <Tooltip title={item.updatedAsOf ? messages({ id: 'CONSENT_LAST_UPDATED' }, { date: formatDate(item.updatedAsOf) }) : undefined}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' width='100%'>
            <AgTypography
              contentStyle='AP.Assistive.Text.SemiBold'
              style={{ color: isAgreed ? AgColor.primaryDark : isRejected ? AgColor.errorMain : AgColor.grey6 }}
            >
              {messages({ id: `CONSENT_${ApiConsentState[item.givenConsent!].toUpperCase()}` })}
            </AgTypography>

            {isMandatoryDisabled ? null : (
              <IconButton size='small' onClick={() => handleViewConsent(item)}>
                <DriveFileRenameOutlineOutlinedIcon color='primary' fontSize='small' />
              </IconButton>
            )}
          </Stack>
        </Tooltip>
      )
    };
  }), [consents, handleViewConsent, messages]);

  useEffect(() => {
    getConsents();
  }, [getConsents]);

  return (
    <S.Container>
      <Stack gap={2}>
        <AgTypography
          contentStyle='AP.H5.UPPERCASE.Bold'
          message={messages({ id: 'MANAGE_CONSENTS' })}
          style={{ color: AgColor.grey6 }}
        />

        {isLoading ? (
          <AgLoading />
        ) : consents.length === 0 ? (
          <AgTypography contentStyle='AP.Paragraph.Small.Regular'>
            {messages({ id: 'CONSENT_NO_CONSENTS' })}
          </AgTypography>
        ) : (
          <AgSimpleTable tableData={tableConsents} />
        )}
      </Stack>
    </S.Container>
  );
};

export default ProfileConsents;
